import { useEffect, useRef, useState } from 'react';
import {Menu, X, Star, ArrowRight, CheckCircle} from 'lucide-react';
import { lumi } from '@/lib/lumi';
import { useSeoMeta } from '@unhead/react';

/* ─── Scroll Reveal Hook ─────────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    // Defer off-screen observer setup until after first paint to reduce TBT
    const raf = requestAnimationFrame(() => {
      const timer = setTimeout(() => {
        const targets = document.querySelectorAll(
          '.reveal-up, .reveal-down, .reveal-left, .reveal-right, .reveal-scale, .reveal-scale-up, .section-heading-line, .stat-item, .service-card-bar'
        );
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.12, rootMargin: '0px 0px -30px 0px' }
        );
        targets.forEach((el) => observer.observe(el));
      }, 120);
      return () => clearTimeout(timer);
    });
    return () => cancelAnimationFrame(raf);
  }, []);
}

/* ─── Animated Counter ───────────────────────────────────────────── */
function useCounter(target: number, duration = 1800, trigger: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {setCount(target);clearInterval(timer);} else
      setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [trigger, target, duration]);
  return count;
}

function StatCounter({ value, suffix, label, delay }: {value: number;suffix: string;label: string;delay: number;}) {
  const ref = useRef<HTMLDivElement>(null);
  const [triggered, setTriggered] = useState(false);
  const count = useCounter(value, 1600, triggered);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setTriggered(true);obs.disconnect();}},
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="stat-item"
      style={{ animationDelay: `${delay}s` }}>
      <div className="text-2xl font-extrabold" style={{ color: '#00C6FF' }}>
        {count}{suffix}
      </div>
      <div className="text-xs text-gray-400 mt-0.5">{label}</div>
    </div>);

}

/* ─── FAQ Accordion Item ─────────────────────────────────────────── */
function FaqItem({ question, answer, delay }: {question: string;answer: string;delay: number;}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="reveal-up rounded-2xl overflow-hidden transition-all duration-300"
      style={{ animationDelay: `${delay}ms`, border: `1px solid ${open ? 'rgba(0,198,255,0.35)' : 'rgba(0,122,255,0.2)'}`, background: open ? 'rgba(0,122,255,0.07)' : 'rgba(255,255,255,0.03)' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left group"
        aria-expanded={open}>
        <span className="font-semibold text-white text-base pr-4 leading-snug">{question}</span>
        <span
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-300"
          style={{ background: open ? 'linear-gradient(135deg,#007AFF,#00C6FF)' : 'rgba(0,122,255,0.2)', transform: open ? 'rotate(45deg)' : 'rotate(0deg)', color: open ? '#fff' : '#00C6FF' }}>
          +
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-400"
        style={{ maxHeight: open ? '400px' : '0', opacity: open ? 1 : 0 }}>
        <p className="px-6 pb-5 text-gray-400 leading-relaxed text-sm">{answer}</p>
      </div>
    </div>);

}

/* ─── Main Component ─────────────────────────────────────────────── */
function Home() {
  useSeoMeta({
    title: 'Professional Web Design Services UK | Fusion Web Design',
    description: 'Expert web design services UK — ecommerce platforms, SaaS apps & brand strategy. Trusted by UK businesses. Get your free quote today.',
    ogTitle: 'Professional Web Design Services UK | Fusion Web Design',
    ogDescription: 'Expert web design services UK — ecommerce, SaaS apps & brand strategy. Free quote.',
    ogImage: 'https://fusionweb.website/og-image.jpg',
    ogUrl: 'https://fusionweb.website/',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Professional Web Design Services UK | Fusion Web Design',
    twitterDescription: 'Expert web design services UK. Free quote today.',
    twitterImage: 'https://fusionweb.website/og-image.jpg',
    canonical: 'https://fusionweb.website/'
  });

  useScrollReveal();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [portfolioVisible, setPortfolioVisible] = useState(false);
  const portfolioRef = useRef<HTMLDivElement>(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [testimonialKey, setTestimonialKey] = useState(0);

  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Web Development',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  // AI Chat state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 80);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Defer portfolio image loading until section is near viewport
  useEffect(() => {
    const el = portfolioRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setPortfolioVisible(true); obs.disconnect(); } },
      { rootMargin: '200px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const services = [
  {
    title: 'Web Development',
    description: 'Custom websites and web applications built with modern technologies. Responsive, fast, and scalable solutions tailored to your business needs.',
    features: ['Custom Design', 'Responsive Layout', 'SEO Optimised', 'Fast Performance'],
    icon: '⚡'
  },
  {
    title: 'Google Marketing',
    description: 'Strategic Google Ads campaigns and SEO services to boost your online visibility and drive qualified traffic to your business.',
    features: ['Google Ads', 'SEO Strategy', 'Analytics', 'Conversion Optimisation'],
    icon: '📈'
  },
  {
    title: 'Social Media Marketing',
    description: 'Comprehensive social media management and advertising across all major platforms to engage your audience and grow your brand.',
    features: ['Content Strategy', 'Ad Campaigns', 'Community Management', 'Brand Growth'],
    icon: '🚀'
  }];


  const projects = [
  { title: 'E-Commerce Platform', category: 'Web Development', image: 'https://static.lumi.new/material/05/0558583218865518a748c6e908c34160.png' },
  { title: 'Corporate Website', category: 'Web Development', image: 'https://static.lumi.new/material/b0/b02c53a98647d0ce7ac68e5b6960887b.png' },
  { title: 'SaaS Application', category: 'Web Development', image: 'https://static.lumi.new/material/f0/f0242334bd83f1bdd3ba16f9a7667c15.png' },
  { title: 'Marketing Campaign', category: 'Google Marketing', image: 'https://static.lumi.new/material/7a/7aa4b8bba24cc0f830f18f659d7f599d.png' },
  { title: 'Brand Strategy', category: 'Social Media', image: 'https://static.lumi.new/material/eb/eb270208dd1bc7a98ca97728a4cbae3b.png' },
  { title: 'Digital Advertising', category: 'Social Media', image: 'https://static.lumi.new/material/c4/c481f350a400c3232dabe80136d8c667.png' }];


  const testimonials = [
  { name: 'Sarah Johnson', company: 'Tech Innovations Inc.', text: 'Fusion Web Design transformed our online presence. Their attention to detail and technical expertise is unmatched. Our website traffic increased by 200% within three months!', rating: 5 },
  { name: 'Michael Chen', company: 'GreenLeaf Organics', text: 'Professional, responsive, and incredibly talented. They delivered exactly what we needed and more. The Google marketing campaign they ran exceeded all our expectations.', rating: 5 },
  { name: 'Emily Rodriguez', company: 'Artistic Vision Studio', text: 'Working with Fusion was a game-changer for our business. Their social media strategy helped us reach thousands of new customers. Highly recommended!', rating: 5 }];


  useEffect(() => {
    // Defer testimonial auto-rotate until after first paint
    const raf = requestAnimationFrame(() => {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        setTestimonialKey((prev) => prev + 1);
      }, 5000);
      return () => clearInterval(interval);
    });
    return () => cancelAnimationFrame(raf);
  }, [testimonials.length]);

  const handleTestimonialChange = (index: number) => {
    setCurrentTestimonial(index);
    setTestimonialKey((prev) => prev + 1);
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormMessage('');

    try {
      await lumi.functions.invoke('sendContactEmail', {
        method: 'POST',
        body: formData
      });

      setFormStatus('success');
      setFormMessage('Thank you! We\'ll get back to you within 24-48 hours.');
      setFormData({ name: '', email: '', phone: '', service: 'Web Development', message: '' });

      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 5000);
    } catch (error: any) {
      setFormStatus('error');
      setFormMessage(error?.data?.error || 'Failed to send message. Please try again.');
    }
  };

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || isChatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatLoading(true);

    try {
      const response = await lumi.functions.invoke('aiChatbot', {
        method: 'POST',
        body: {
          question: userMessage,
          chatHistory: chatMessages
        }
      });

      setChatMessages(prev => [...prev, { role: 'assistant', content: response.answer }]);
    } catch (error: any) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: error?.data?.error || 'Sorry, I encountered an error. Please try again or contact us directly.' 
      }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#001F3F' }}>
      {/* ── Logo Banner ────────────────────────────────────────────── */}
      <div
        className="w-full flex flex-col justify-center items-center py-14 px-4 relative overflow-hidden"
        style={{ backgroundColor: '#000000', minHeight: '260px' }}>

        <div className="absolute inset-0 pointer-events-none glow-pulse"
        style={{ background: 'radial-gradient(ellipse 70% 90% at 50% 50%, rgba(0,122,255,0.13) 0%, transparent 70%)' }} />

        <div className="absolute pointer-events-none ring-expand"
        style={{ top: '50%', left: '50%', width: 'clamp(260px,40vw,520px)', height: 'clamp(260px,40vw,520px)', border: '1.5px solid rgba(0,198,255,0.25)', borderRadius: '50%' }} />
        <div className="absolute pointer-events-none ring-expand-delay"
        style={{ top: '50%', left: '50%', width: 'clamp(260px,40vw,520px)', height: 'clamp(260px,40vw,520px)', border: '1.5px solid rgba(0,122,255,0.2)', borderRadius: '50%' }} />

        {[
        { b: '20%', l: '30%', cls: 'particle-1', bg: '#00C6FF', s: '0 0 6px #00C6FF', sz: 'w-1.5 h-1.5' },
        { b: '30%', l: '60%', cls: 'particle-2', bg: '#007AFF', s: '0 0 6px #007AFF', sz: 'w-1 h-1' },
        { b: '15%', l: '45%', cls: 'particle-3', bg: '#00C6FF', s: '0 0 8px #00C6FF', sz: 'w-2 h-2' },
        { b: '25%', l: '22%', cls: 'particle-4', bg: '#007AFF', s: '0 0 5px #007AFF', sz: 'w-1 h-1' },
        { b: '20%', l: '75%', cls: 'particle-5', bg: '#00C6FF', s: '0 0 6px #00C6FF', sz: 'w-1.5 h-1.5' }].
        map((p, i) =>
        <div key={i} className="absolute pointer-events-none" style={{ bottom: p.b, left: p.l }}>
            <div className={`${p.cls} ${p.sz} rounded-full`} style={{ background: p.bg, boxShadow: p.s }} />
          </div>
        )}

        <div className="absolute pointer-events-none scan-sweep"
        style={{ top: '50%', left: 0, width: '40%', height: '1px', background: 'linear-gradient(90deg,transparent,rgba(0,198,255,0.7),transparent)', marginTop: '-0.5px' }} />

        <div className="relative z-10 logo-float fade-slide-down">
          <div className="absolute inset-0 pointer-events-none"
          style={{ filter: 'blur(32px)', background: 'radial-gradient(ellipse 60% 50% at 50% 60%, rgba(0,198,255,0.22) 0%, transparent 70%)', transform: 'scale(1.3)' }} />
          <img
            src="https://static.lumi.new/material/eb/ebfaca16edb4c088a017c193db3c4cde.png"
            alt="Fusion Web Design Logo"
            width="290"
            height="290"
            sizes="(max-width: 768px) 150px, 290px"
            className="relative w-auto object-contain drop-shadow-2xl"
            style={{ height: 'clamp(150px,22vw,290px)', maxWidth: '290px', filter: 'drop-shadow(0 0 24px rgba(0,198,255,0.45)) drop-shadow(0 0 8px rgba(0,122,255,0.6))' }} />
        </div>

        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px border-glow"
        style={{ width: 'clamp(200px,40vw,480px)', background: 'linear-gradient(90deg,transparent,#007AFF,#00C6FF,#007AFF,transparent)' }} />

        {[['top-4 left-6', 'border-t-2 border-l-2'], ['top-4 right-6', 'border-t-2 border-r-2'], ['bottom-4 left-6', 'border-b-2 border-l-2'], ['bottom-4 right-6', 'border-b-2 border-r-2']].map(([pos, borders], i) =>
        <div key={i} className={`absolute ${pos} w-6 h-6 ${borders} border-glow`} style={{ borderColor: 'rgba(0,198,255,0.5)' }} />
        )}
      </div>

      {/* ── Navigation ─────────────────────────────────────────────── */}
      <nav
        className="sticky top-0 w-full z-50 transition-all duration-300"
        style={{
          backgroundColor: isScrolled ? 'rgba(0,31,63,0.97)' : '#001F3F',
          backdropFilter: isScrolled ? 'blur(8px)' : 'none',
          boxShadow: isScrolled ? '0 2px 24px rgba(0,122,255,0.15)' : 'none',
          borderBottom: '1px solid rgba(0,198,255,0.15)'
        }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-16 relative">
            <div className="hidden md:flex items-center space-x-8">
              {['home', 'services', 'process', 'portfolio', 'testimonials', 'faq'].map((item) =>
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className="nav-link-animated capitalize text-gray-300 hover:text-white transition-colors duration-200 text-sm font-medium"
                style={{ letterSpacing: '0.03em' }}>
                  {item === 'faq' ? 'FAQ' : item}
                </button>
              )}
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-glow px-6 py-2.5 rounded-lg font-semibold text-white text-sm transition-all duration-200 hover:opacity-90 hover:shadow-lg"
                style={{ background: 'linear-gradient(90deg,#007AFF,#00C6FF)', boxShadow: '0 0 16px rgba(0,198,255,0.25)' }}>
                Contact Us
              </button>
            </div>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white p-2 absolute right-4"
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {isMenuOpen &&
        <div id="mobile-menu" className="md:hidden border-t" style={{ backgroundColor: '#001F3F', borderColor: 'rgba(0,198,255,0.15)' }}>
            <div className="px-4 py-6 space-y-4">
              {['home', 'services', 'process', 'portfolio', 'testimonials', 'faq'].map((item) =>
            <button key={item} onClick={() => scrollToSection(item)}
            className="block w-full text-left text-gray-300 hover:text-white capitalize py-2 transition-colors duration-200">
                  {item === 'faq' ? 'FAQ' : item}
                </button>
            )}
              <button onClick={() => scrollToSection('contact')}
            className="block w-full text-left px-6 py-2.5 rounded-lg font-semibold text-white"
            style={{ background: 'linear-gradient(90deg,#007AFF,#00C6FF)' }}>
                Contact Us
              </button>
            </div>
          </div>
        }
      </nav>

      <main>

        {/* ... keep existing code */}

        {/* ── Hero ───────────────────────────────────────────────────── */}
        <section id="home" className="relative pt-24 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 50% at 60% 40%, rgba(0,122,255,0.18) 0%, transparent 70%)' }} />
          <div className="absolute top-20 right-10 w-72 h-72 rounded-full opacity-10 float-slow pointer-events-none"
          style={{ background: 'radial-gradient(circle,#00C6FF,transparent)' }} />
          <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full opacity-10 float-med pointer-events-none"
          style={{ background: 'radial-gradient(circle,#007AFF,transparent)' }} />

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">

              {/* Left */}
              <div className="space-y-8">
                <div className="hero-tagline inline-block px-4 py-1.5 rounded-full text-sm font-semibold"
                style={{ background: 'rgba(0,122,255,0.15)', color: '#00C6FF', border: '1px solid rgba(0,198,255,0.3)' }}>
                  Professional Digital Agency
                </div>
                <h1 className="hero-heading text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight" style={{ animationFillMode: 'none' }}>
                  Fusion Web Design — Expert Web Development & Digital Marketing
                </h1>
                <p className="hero-body text-xl text-gray-300 leading-relaxed max-w-lg">
                  Our expert <strong>web design</strong> team crafts high-converting websites for UK businesses — from ecommerce to SaaS platforms and brand strategy.
                </p>
                <div className="hero-buttons flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="btn-glow px-8 py-4 rounded-xl font-semibold text-lg text-white flex items-center justify-center space-x-2 transition-all duration-200 hover:opacity-90 hover:shadow-2xl"
                    style={{ background: 'linear-gradient(90deg,#007AFF,#00C6FF)', boxShadow: '0 4px 24px rgba(0,198,255,0.3)' }}>
                    <span>Get Started</span>
                    <ArrowRight size={20} />
                  </button>
                  <button
                    onClick={() => scrollToSection('portfolio')}
                    className="px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:bg-white/5 hover:scale-105"
                    style={{ border: '2px solid #007AFF', color: '#00C6FF' }}>
                    View Our Work
                  </button>
                </div>

                {/* Stats */}
                <div className="hero-stats flex gap-8 pt-2">
                  <StatCounter value={50} suffix="+" label="Projects" delay={0.8} />
                  <StatCounter value={100} suffix="%" label="Client Satisfaction" delay={0.95} />
                  <StatCounter value={3} suffix="+" label="Years Experience" delay={1.1} />
                </div>
              </div>

              {/* Right */}
              <div className="relative hero-image">
                <div className="rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 0 60px rgba(0,122,255,0.25)', border: '1px solid rgba(0,198,255,0.2)' }}>
                  <img
                    src="https://static.lumi.new/material/f0/f0242334bd83f1bdd3ba16f9a7667c15.png"
                    alt="Web Development — Fusion Web Design UK"
                    loading="eager"
                    decoding="sync"
                    fetchpriority="high"
                    elementtiming="hero-image"
                    width="560"
                    height="420"
                    sizes="(max-width: 640px) calc(100vw - 32px), (max-width: 1024px) calc(50vw - 48px), 560px"
                    className="w-full h-full object-cover aspect-[4/3] transition-transform duration-700 hover:scale-105"
                    style={{ opacity: 1, maxWidth: '560px' }} />
                  <div className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{ background: 'linear-gradient(135deg,rgba(0,122,255,0.15) 0%,transparent 60%)' }} />
                </div>
                <div className="hero-badge absolute -bottom-4 -left-4 px-4 py-3 rounded-xl shadow-xl"
                style={{ background: 'linear-gradient(135deg,#007AFF,#00C6FF)', boxShadow: '0 8px 32px rgba(0,122,255,0.4)' }}>
                  <div className="text-xs font-semibold text-white/80">Average ROI</div>
                  <div className="text-xl font-extrabold text-white">+200%</div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── Services ───────────────────────────────────────────────── */}
        <section id="services" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#00132B' }}>
          <div className="max-w-7xl mx-auto">

            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Our Services</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 max-w-2xl mx-auto mt-4">
                Comprehensive digital solutions to elevate your brand and drive growth
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, index) =>
              <div
                key={index}
                className={`reveal-scale-up delay-${(index + 1) * 150} card-hover rounded-2xl p-8 group`}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(0,122,255,0.2)',
                  boxShadow: '0 2px 20px rgba(0,31,63,0.5)',
                  transition: 'border-color 0.3s ease, transform 0.35s cubic-bezier(0.22,0.68,0,1.2), box-shadow 0.35s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(0,198,255,0.5)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(0,122,255,0.2)'}>
                  <div className="text-3xl mb-4 transition-transform duration-300 group-hover:scale-110 inline-block">{service.icon}</div>
                  <div className="service-card-bar h-1 rounded-full mb-6" style={{ background: 'linear-gradient(90deg,#007AFF,#00C6FF)' }} />
                  <h3 className="text-2xl font-bold mb-4" style={{ color: '#00C6FF' }}>{service.title}</h3>
                  <p className="text-gray-400 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) =>
                  <li key={idx} className="flex items-center space-x-2 text-gray-300 transition-transform duration-200 hover:translate-x-1">
                        <CheckCircle size={16} style={{ color: '#007AFF', flexShrink: 0 }} />
                        <span>{feature}</span>
                      </li>
                  )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── Portfolio ──────────────────────────────────────────────── */}
        <section id="portfolio" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#001F3F' }}>
          <div ref={portfolioRef} className="max-w-7xl mx-auto">

            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Our Work</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 max-w-2xl mx-auto mt-4">
                Showcasing projects that combine creativity with technical excellence
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, index) =>
              <div
                key={index}
                className={`reveal-scale-up delay-${index % 3 * 150 + 100} portfolio-card group relative overflow-hidden rounded-2xl aspect-[4/3] cursor-pointer`}
                style={{ border: '1px solid rgba(0,122,255,0.15)', transition: 'border-color 0.3s ease, box-shadow 0.3s ease' }}
                onMouseEnter={(e) => {e.currentTarget.style.borderColor = 'rgba(0,198,255,0.4)';e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,122,255,0.2)';}}
                onMouseLeave={(e) => {e.currentTarget.style.borderColor = 'rgba(0,122,255,0.15)';e.currentTarget.style.boxShadow = 'none';}}>
                  <picture>
                    <source
                      srcSet={portfolioVisible ? `${project.image}?format=webp` : undefined}
                      type="image/webp"
                    />
                    <img
                      src={portfolioVisible ? project.image : undefined}
                      alt={project.title}
                      loading="lazy"
                      fetchpriority="low"
                      decoding="async"
                      width="560"
                      height="420"
                      sizes="(max-width: 640px) calc(100vw - 32px), (max-width: 1024px) calc(50vw - 28px), calc(33vw - 24px)"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ maxWidth: '560px' }} />
                  </picture>
                  <div
                  className="portfolio-overlay absolute inset-0"
                  style={{ background: 'linear-gradient(to top,#001F3F 0%,rgba(0,31,63,0.5) 50%,transparent 100%)' }} />
                  <div className="portfolio-info absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-sm font-semibold mb-1" style={{ color: '#00C6FF' }}>{project.category}</p>
                    <h3 className="text-xl font-bold text-white">{project.title}</h3>
                  </div>
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 scale-0 group-hover:scale-100"
                style={{ background: 'linear-gradient(135deg,#007AFF,#00C6FF)' }} />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── Testimonials ───────────────────────────────────────────── */}
        <section id="testimonials" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#00132B' }}>
          <div className="max-w-5xl mx-auto">

            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Client Testimonials</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 mt-4">Hear what our clients say about working with us</p>
            </div>

            <div className="reveal-scale-up delay-300 relative">
              <div
                key={testimonialKey}
                className="testimonial-enter rounded-2xl p-8 md:p-12"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(0,122,255,0.25)',
                  boxShadow: '0 8px 48px rgba(0,122,255,0.1)'
                }}>
                <div className="text-7xl font-serif leading-none mb-2 opacity-20" style={{ color: '#007AFF' }}>"</div>
                <div className="flex mb-4 gap-1">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) =>
                  <Star
                    key={i}
                    size={20}
                    className="star-pop"
                    style={{ fill: '#007AFF', color: '#007AFF', animationDelay: `${i * 0.08}s` }} />
                  )}
                </div>
                <p className="text-xl text-gray-200 mb-8 leading-relaxed italic">
                  "{testimonials[currentTestimonial].text}"
                </p>
                <div className="flex items-center space-x-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg,#007AFF,#00C6FF)' }}>
                    {testimonials[currentTestimonial].name[0]}
                  </div>
                  <div>
                    <p className="font-bold text-white">{testimonials[currentTestimonial].name}</p>
                    <p className="text-sm" style={{ color: '#00C6FF' }}>{testimonials[currentTestimonial].company}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mt-8 space-x-2" role="tablist" aria-label="Testimonials navigation">
                {testimonials.map((t, index) =>
                <button
                  key={index}
                  onClick={() => handleTestimonialChange(index)}
                  role="tab"
                  aria-selected={index === currentTestimonial}
                  aria-label={`View testimonial from ${t.name}`}
                  className="h-2 rounded-full transition-all duration-400"
                  style={{
                    width: index === currentTestimonial ? '2rem' : '0.75rem',
                    background: index === currentTestimonial ? 'linear-gradient(90deg,#007AFF,#00C6FF)' : 'rgba(255,255,255,0.2)'
                  }} />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── Why Choose Us ─────────────────────────────────────────── */}
        <section id="why-us" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#001F3F' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Why UK Businesses Trust Our Web Design Services</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 max-w-3xl mx-auto mt-4">
                We don't just build websites — we engineer digital experiences that convert visitors into paying customers. Here's what sets us apart from the competition.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
              <div className="space-y-6 order-2 md:order-1">
                <div className="reveal-left delay-100 flex items-start gap-4 p-6 rounded-2xl" style={{ background: 'rgba(0,122,255,0.06)', border: '1px solid rgba(0,122,255,0.18)' }}>
                  <div className="text-3xl flex-shrink-0">🎯</div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#00C6FF' }}>Results-Driven Web Development</h3>
                    <p className="text-gray-400 leading-relaxed">Every website we build is engineered with one goal: to generate business. We combine clean code, strategic UX design, and conversion-focused layouts to ensure your website works as hard as you do. Our development process is transparent, deadline-driven, and built around your business objectives.</p>
                  </div>
                </div>
                <div className="reveal-left delay-200 flex items-start gap-4 p-6 rounded-2xl" style={{ background: 'rgba(0,122,255,0.06)', border: '1px solid rgba(0,122,255,0.18)' }}>
                  <div className="text-3xl flex-shrink-0">⚙️</div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#00C6FF' }}>Modern Tech Stack, Future-Proof Builds</h3>
                    <p className="text-gray-400 leading-relaxed">We use the latest web technologies including React, Next.js, TypeScript, and headless CMS platforms to build websites that are fast, scalable, and easy to maintain. No outdated WordPress themes or cookie-cutter templates — every project is crafted from scratch to your exact specifications.</p>
                  </div>
                </div>
                <div className="reveal-left delay-300 flex items-start gap-4 p-6 rounded-2xl" style={{ background: 'rgba(0,122,255,0.06)', border: '1px solid rgba(0,122,255,0.18)' }}>
                  <div className="text-3xl flex-shrink-0">📊</div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#00C6FF' }}>Data-Backed Digital Marketing</h3>
                    <p className="text-gray-400 leading-relaxed">Our marketing strategies are grounded in real data. From Google Analytics 4 to Meta Ads Manager insights, we track every click, impression, and conversion — then optimise relentlessly to stretch your budget further and deliver measurable ROI month after month.</p>
                  </div>
                </div>
              </div>
              <div className="reveal-right delay-200 space-y-6 order-1 md:order-2">
                <div className="flex items-start gap-4 p-6 rounded-2xl" style={{ background: 'rgba(0,122,255,0.06)', border: '1px solid rgba(0,122,255,0.18)' }}>
                  <div className="text-3xl flex-shrink-0">🤝</div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#00C6FF' }}>Dedicated UK-Based Support</h3>
                    <p className="text-gray-400 leading-relaxed">Based in the UK, we understand the British market — consumer behaviour, local SEO, regional advertising nuances, and compliance requirements. You'll always have direct access to your project team, with no offshore handoffs or communication delays.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-6 rounded-2xl" style={{ background: 'rgba(0,122,255,0.06)', border: '1px solid rgba(0,122,255,0.18)' }}>
                  <div className="text-3xl flex-shrink-0">🚀</div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#00C6FF' }}>Fast Turnaround, No Compromise on Quality</h3>
                    <p className="text-gray-400 leading-relaxed">We know time is money. Our streamlined project management process means your website goes live faster than the industry average — without cutting corners on design quality, performance scores, or accessibility standards. Most projects are completed in 2–4 weeks.</p>
                  </div>
                </div>
                {/* Why Choose Us — feature image */}
                <div className="reveal-right delay-350 rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,198,255,0.25)', boxShadow: '0 8px 40px rgba(0,122,255,0.15)' }}>
                  





                </div>
                <div className="flex items-start gap-4 p-6 rounded-2xl" style={{ background: 'rgba(0,122,255,0.06)', border: '1px solid rgba(0,122,255,0.18)' }}>
                  <div className="text-3xl flex-shrink-0">🔒</div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#00C6FF' }}>Security & Performance as Standard</h3>
                    <p className="text-gray-400 leading-relaxed">Every website we deliver is SSL secured, GDPR compliant, and optimised to achieve 90+ Google PageSpeed scores. We implement best-in-class security headers, lazy loading, image optimisation, and CDN delivery as standard — not as expensive add-ons.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── Web Development Process ─────────────────────────────────── */}
        <section id="process" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#00132B' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Our Web Design & Development Process</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 max-w-3xl mx-auto mt-4">
                From initial concept to live launch, we follow a proven 6-step development process that ensures your project is delivered on time, on budget, and beyond expectations.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {[
              { step: '01', icon: '💬', title: 'Discovery & Strategy', desc: 'We start by understanding your business, target audience, competitors, and goals. This discovery phase shapes every decision we make — from sitemap architecture to colour palette choices. A thorough brief means zero surprises later.' },
              { step: '02', icon: '🖌️', title: 'UI/UX Design & Wireframing', desc: 'Our designers create high-fidelity wireframes and interactive prototypes before a single line of code is written. You see exactly what your website will look like and how users will navigate it — with full revisions included.' },
              { step: '03', icon: '💻', title: 'Development & Build', desc: 'Our developers bring the approved design to life using clean, semantic HTML, modern CSS, and performant JavaScript frameworks. Every component is built to be responsive, accessible, and blazingly fast across all devices.' },
              { step: '04', icon: '🔍', title: 'SEO & Content Integration', desc: 'We integrate on-page SEO from the ground up — optimised title tags, meta descriptions, structured data markup, canonical URLs, XML sitemaps, and keyword-rich content — giving your site the best possible foundation for Google rankings.' },
              { step: '05', icon: '🧪', title: 'Testing & Quality Assurance', desc: 'Before launch, every page is rigorously tested across browsers, devices, and screen sizes. We run Lighthouse audits, check Core Web Vitals, validate forms, test load times, and review every interaction to ensure a flawless experience.' },
              { step: '06', icon: '🚀', title: 'Launch & Ongoing Support', desc: 'Launch day is just the beginning. We handle DNS configuration, CDN setup, SSL certificates, and Google Search Console submission. Post-launch, we offer ongoing maintenance, performance monitoring, and growth-focused updates.' }].
              map((item, i) =>
              <div
                key={i}
                className={`reveal-scale-up delay-${i % 3 * 150 + 100} card-hover rounded-2xl p-8 relative overflow-hidden group`}
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.2)' }}>
                  <div className="absolute top-4 right-4 text-6xl font-black opacity-5 select-none" style={{ color: '#007AFF' }}>{item.step}</div>
                  <div className="text-3xl mb-3">{item.icon}</div>
                  <div className="service-card-bar h-1 rounded-full mb-4" style={{ background: 'linear-gradient(90deg,#007AFF,#00C6FF)', width: '40px' }} />
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#00C6FF' }}>{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm">{item.desc}</p>
                </div>
              )}
            </div>

            {/* Process — visual showcase */}
            <div className="reveal-up delay-200 grid md:grid-cols-2 gap-6 mb-10">
              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,198,255,0.2)', boxShadow: '0 4px 30px rgba(0,122,255,0.12)' }}>
                <picture>
                  <source
                    srcSet="https://static.lumi.new/material/eb/eb270208dd1bc7a98ca97728a4cbae3b.png?format=webp"
                    type="image/webp"
                  />
                  <img
                    src="https://static.lumi.new/material/eb/eb270208dd1bc7a98ca97728a4cbae3b.png"
                    alt="Web development process — design and build phase"
                    loading="lazy"
                    fetchpriority="low"
                    decoding="async"
                    width="800"
                    height="450"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="w-full object-cover aspect-video transition-transform duration-700 hover:scale-105" />
                </picture>
              </div>
              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,198,255,0.2)', boxShadow: '0 4px 30px rgba(0,122,255,0.12)' }}>
                <picture>
                  <source
                    srcSet="https://static.lumi.new/material/c4/c481f350a400c3232dabe80136d8c667.png?format=webp"
                    type="image/webp"
                  />
                  <img
                    src="https://static.lumi.new/material/c4/c481f350a400c3232dabe80136d8c667.png"
                    alt="Web development process — launch and growth phase"
                    loading="lazy"
                    fetchpriority="low"
                    decoding="async"
                    width="800"
                    height="450"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="w-full object-cover aspect-video transition-transform duration-700 hover:scale-105" />
                </picture>
              </div>
            </div>

            {/* Web Dev Tech Stack */}
            <div className="reveal-up delay-300 rounded-2xl p-8 md:p-10" style={{ background: 'rgba(0,122,255,0.06)', border: '1px solid rgba(0,198,255,0.2)' }}>
              <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: '#00C6FF' }}>Technologies We Work With</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                {[
                { name: 'React', icon: '⚛️' },
                { name: 'TypeScript', icon: '🔷' },
                { name: 'Next.js', icon: '▲' },
                { name: 'Node.js', icon: '🟢' },
                { name: 'Tailwind CSS', icon: '🎨' },
                { name: 'MongoDB', icon: '🍃' },
                { name: 'PostgreSQL', icon: '🐘' },
                { name: 'Shopify', icon: '🛒' },
                { name: 'WordPress', icon: '📝' },
                { name: 'Vercel', icon: '▲' },
                { name: 'AWS', icon: '☁️' },
                { name: 'Stripe', icon: '💳' }].
                map((tech, i) =>
                <div key={i} className="flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-300 hover:-translate-y-1 cursor-default"
                style={{ background: 'rgba(0,31,63,0.6)', border: '1px solid rgba(0,122,255,0.15)' }}>
                    <span className="text-2xl">{tech.icon}</span>
                    <span className="text-xs font-semibold text-gray-300">{tech.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── Social Media Advertising Deep-Dive ─────────────────────── */}
        <section id="social-advertising" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#001F3F' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Social Media Advertising That Delivers Real Results</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 max-w-3xl mx-auto mt-4">
                With over 57 million social media users in the UK alone, there has never been a better time to invest in targeted social advertising. We create campaigns that reach the right people, at the right time, with the right message.
              </p>
            </div>

            {/* Social Advertising — hero image */}
            <div className="reveal-up delay-100 mb-12 rounded-2xl overflow-hidden relative" style={{ border: '1px solid rgba(0,198,255,0.25)', boxShadow: '0 8px 48px rgba(0,122,255,0.2)' }}>
              <picture>
                <source
                  srcSet="https://static.lumi.new/material/c4/c481f350a400c3232dabe80136d8c667.png?format=webp"
                  type="image/webp"
                />
                <img
                  src="https://static.lumi.new/material/c4/c481f350a400c3232dabe80136d8c667.png"
                  alt="Social media advertising campaigns — Facebook, Instagram, TikTok, LinkedIn"
                  loading="lazy"
                  fetchpriority="low"
                  decoding="async"
                  width="1200"
                  height="360"
                  sizes="(max-width: 768px) 100vw, 1200px"
                  className="w-full object-cover"
                  style={{ maxHeight: '360px', maxWidth: '100%', objectPosition: 'center' }} />
              </picture>
              <div className="absolute inset-0 flex items-end" style={{ background: 'linear-gradient(to top, rgba(0,31,63,0.92) 0%, transparent 60%)' }}>
                <div className="p-8">
                  <p className="text-2xl md:text-3xl font-extrabold text-white max-w-2xl leading-snug">Precision-targeted ad campaigns that reach your ideal customers across every major platform.</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Meta / Facebook Ads */}
              <div className="reveal-left delay-100 rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.2)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: 'linear-gradient(135deg,#1877F2,#42b0ff)' }}>f</div>
                  <h3 className="text-2xl font-bold text-white">Facebook & Instagram Ads</h3>
                </div>
                <p className="text-gray-400 leading-relaxed mb-4">Meta's advertising platform gives you access to over 3 billion monthly active users. We craft precision-targeted Facebook and Instagram ad campaigns using detailed audience segmentation — demographics, interests, behaviours, and powerful Custom Audiences built from your existing customer data.</p>
                <ul className="space-y-2">
                  {['Carousel, video, story & reel ad formats', 'Lookalike Audiences to find new customers', 'Retargeting campaigns for warm audiences', 'A/B split testing for maximum performance', 'Pixel tracking & conversion optimisation', 'Monthly performance reports with clear insights'].map((item, i) =>
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle size={15} style={{ color: '#007AFF', flexShrink: 0 }} />
                      <span>{item}</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Google Ads */}
              <div className="reveal-right delay-100 rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.2)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: 'linear-gradient(135deg,#EA4335,#FBBC05)' }}>G</div>
                  <h3 className="text-2xl font-bold text-white">Google Ads & PPC Management</h3>
                </div>
                <p className="text-gray-400 leading-relaxed mb-4">Google processes over 8.5 billion searches per day. We put your business in front of high-intent buyers at exactly the moment they're searching for your products or services. Our Google Ads campaigns are structured for maximum Quality Score, lower cost-per-click, and higher conversion rates.</p>
                <ul className="space-y-2">
                  {['Search, display, shopping & performance max', 'Negative keyword management to reduce waste', 'Smart bidding strategies (ROAS / CPA targets)', 'Ad copy A/B testing & landing page alignment', 'Google Analytics 4 & Tag Manager setup', 'Transparent monthly spend & ROI reporting'].map((item, i) =>
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle size={15} style={{ color: '#007AFF', flexShrink: 0 }} />
                      <span>{item}</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* TikTok Ads */}
              <div className="reveal-left delay-200 rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.2)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl font-bold" style={{ background: 'linear-gradient(135deg,#000,#ff0050)' }}>T</div>
                  <h3 className="text-2xl font-bold text-white">TikTok & Short-Form Video Ads</h3>
                </div>
                <p className="text-gray-400 leading-relaxed mb-4">TikTok's explosive growth makes it one of the most powerful advertising platforms available today, especially for reaching 18–34 year olds. We create scroll-stopping short-form video content and TikTok ad campaigns that build brand awareness, drive traffic, and generate real sales.</p>
                <ul className="space-y-2">
                  {['In-feed video ads & branded content', 'TopView & Spark Ads for maximum reach', 'Creator partnership & UGC strategy', 'TikTok Pixel setup & event tracking', 'Trend-led creative strategy', 'Full campaign management & optimisation'].map((item, i) =>
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle size={15} style={{ color: '#007AFF', flexShrink: 0 }} />
                      <span>{item}</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* LinkedIn Ads */}
              <div className="reveal-right delay-200 rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.2)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl font-bold" style={{ background: 'linear-gradient(135deg,#0077B5,#00a0dc)' }}>in</div>
                  <h3 className="text-2xl font-bold text-white">LinkedIn B2B Advertising</h3>
                </div>
                <p className="text-gray-400 leading-relaxed mb-4">For B2B companies looking to reach decision-makers, LinkedIn Ads are unmatched. We build LinkedIn campaigns targeting by job title, company size, industry, and seniority — ensuring your message reaches CEOs, directors, and buyers who have the authority and budget to act.</p>
                <ul className="space-y-2">
                  {['Sponsored content & message ads', 'Lead gen forms for frictionless conversions', 'Account-based marketing (ABM) campaigns', 'Thought leadership content promotion', 'Company page growth strategies', 'B2B funnel tracking & reporting'].map((item, i) =>
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle size={15} style={{ color: '#007AFF', flexShrink: 0 }} />
                      <span>{item}</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Stats Belt */}
            <div className="reveal-up delay-300 grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
              { value: '57M+', label: 'UK Social Media Users', icon: '👥' },
              { value: '4.4x', label: 'Average Facebook Ads ROAS', icon: '📈' },
              { value: '£2.69', label: 'Avg. Google CPC (UK)', icon: '🎯' },
              { value: '3.7%', label: 'Avg. Instagram Engagement Rate', icon: '❤️' }].
              map((stat, i) =>
              <div key={i} className="text-center p-6 rounded-2xl" style={{ background: 'rgba(0,122,255,0.08)', border: '1px solid rgba(0,198,255,0.2)' }}>
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-extrabold mb-1" style={{ color: '#00C6FF' }}>{stat.value}</div>
                  <div className="text-xs text-gray-400">{stat.label}</div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── SEO & Content Marketing ─────────────────────────────────── */}
        <section id="seo" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#00132B' }}>
          <div className="max-w-7xl mx-auto">

            {/* SEO — split image/text intro */}
            <div className="reveal-up grid md:grid-cols-2 gap-10 items-center mb-16">
              <div>
                <h2 className="text-4xl md:text-5xl font-extrabold mb-4">SEO & Content Marketing Services</h2>
                <span className="section-heading-line mb-4 block" />
                <p className="text-xl text-gray-400 mt-4 leading-relaxed">
                  Ranking on page one of Google doesn't happen by accident. It requires a strategic, sustained approach that combines technical SEO, high-quality content, and authoritative link building. We deliver all three.
                </p>
              </div>
              <div className="reveal-right delay-200 rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,198,255,0.25)', boxShadow: '0 8px 40px rgba(0,122,255,0.15)' }}>
                <picture>
                  <source
                    srcSet="https://static.lumi.new/material/eb/eb270208dd1bc7a98ca97728a4cbae3b.png?format=webp"
                    type="image/webp"
                  />
                  <img
                    src="https://static.lumi.new/material/eb/eb270208dd1bc7a98ca97728a4cbae3b.png"
                    alt="SEO content marketing strategy and results"
                    loading="lazy"
                    fetchpriority="low"
                    decoding="async"
                    width="800"
                    height="450"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="w-full object-cover aspect-video transition-transform duration-700 hover:scale-105" />
                </picture>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              <div className="reveal-scale-up delay-100 rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.2)' }}>
                <div className="text-4xl mb-4">🔧</div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#00C6FF' }}>Technical SEO Audits</h3>
                <p className="text-gray-400 leading-relaxed mb-4">We conduct thorough technical SEO audits covering Core Web Vitals, crawlability, indexation, site architecture, schema markup, canonical tags, page speed, and mobile responsiveness — identifying every issue holding your rankings back.</p>
                <ul className="space-y-1.5">
                  {['Core Web Vitals optimisation', 'XML sitemap & robots.txt setup', 'Structured data / schema markup', 'Internal linking architecture', 'Duplicate content resolution', 'Mobile-first indexing compliance'].map((t, i) =>
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm"><CheckCircle size={14} style={{ color: '#007AFF', flexShrink: 0 }} /><span>{t}</span></li>
                  )}
                </ul>
              </div>
              <div className="reveal-scale-up delay-200 rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.2)' }}>
                <div className="text-4xl mb-4">✍️</div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#00C6FF' }}>Content Strategy & Creation</h3>
                <p className="text-gray-400 leading-relaxed mb-4">Content is the engine of organic growth. We develop data-driven content strategies based on keyword research, search intent analysis, and competitor gap analysis — then create authoritative, long-form content that ranks, engages, and converts.</p>
                <ul className="space-y-1.5">
                  {['Keyword research & intent mapping', 'Long-form blog posts & guides', 'Landing page copywriting', 'Product & category page content', 'FAQ schema content', 'Content calendar management'].map((t, i) =>
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm"><CheckCircle size={14} style={{ color: '#007AFF', flexShrink: 0 }} /><span>{t}</span></li>
                  )}
                </ul>
              </div>
              <div className="reveal-scale-up delay-300 rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.2)' }}>
                <div className="text-4xl mb-4">🔗</div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#00C6FF' }}>Link Building & Authority</h3>
                <p className="text-gray-400 leading-relaxed mb-4">Backlinks remain one of Google's strongest ranking signals. We build high-quality, relevant backlinks through ethical outreach, digital PR, guest posting, and industry directory listings — systematically increasing your domain authority and search visibility.</p>
                <ul className="space-y-1.5">
                  {['White-hat outreach campaigns', 'Digital PR & brand mentions', 'Guest posting on authority sites', 'Competitor backlink analysis', 'Local citation building', 'Monthly link acquisition reports'].map((t, i) =>
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm"><CheckCircle size={14} style={{ color: '#007AFF', flexShrink: 0 }} /><span>{t}</span></li>
                  )}
                </ul>
              </div>
            </div>

            {/* SEO Results Banner */}
            <div className="reveal-up delay-300 rounded-2xl p-8 md:p-12 text-center" style={{ background: 'linear-gradient(135deg, rgba(0,122,255,0.12) 0%, rgba(0,198,255,0.08) 100%)', border: '1px solid rgba(0,198,255,0.25)' }}>
              <h3 className="text-3xl font-extrabold mb-4">The Numbers Speak for Themselves</h3>
              <p className="text-gray-400 max-w-2xl mx-auto mb-8">Our clients consistently see dramatic improvements in organic traffic, keyword rankings, and revenue after engaging our SEO services. Here's what a typical 6-month engagement looks like:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                { val: '+247%', lbl: 'Average Organic Traffic Increase' },
                { val: 'Top 3', lbl: 'Google Rankings for Target Keywords' },
                { val: '-42%', lbl: 'Reduction in Bounce Rate' },
                { val: '+3.8x', lbl: 'Increase in Qualified Leads' }].
                map((s, i) =>
                <div key={i}>
                    <div className="text-3xl md:text-4xl font-extrabold mb-1 shimmer-text">{s.val}</div>
                    <div className="text-sm text-gray-400">{s.lbl}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── Industry Sectors ────────────────────────────────────────── */}
        <section id="sectors" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#001F3F' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Industries We Serve</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 max-w-3xl mx-auto mt-4">
                We've delivered digital solutions for businesses across a wide range of UK industries. Our sector experience means we understand your market, your customers, and the competitive landscape you operate in.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
              { icon: '🛒', name: 'E-Commerce & Retail', desc: 'High-converting online stores built on Shopify, WooCommerce, and custom platforms.' },
              { icon: '🏥', name: 'Health & Wellness', desc: 'Booking systems, patient portals, and GDPR-compliant healthcare websites.' },
              { icon: '🏗️', name: 'Construction & Trades', desc: 'Lead generation websites and Google Ads for builders, electricians & plumbers.' },
              { icon: '🍽️', name: 'Hospitality & Food', desc: 'Restaurant websites, online menus, reservation systems and local SEO.' },
              { icon: '💼', name: 'Professional Services', desc: 'Authority-building websites for law firms, accountants, and consultancies.' },
              { icon: '🏠', name: 'Property & Real Estate', desc: 'Property listing platforms, virtual tour integrations, and estate agent SEO.' },
              { icon: '🎓', name: 'Education & Training', desc: 'Course platforms, LMS integrations, and student recruitment campaigns.' },
              { icon: '🚗', name: 'Automotive', desc: 'Vehicle listings, service booking systems, and dealer PPC campaigns.' },
              { icon: '💻', name: 'Tech & SaaS', desc: 'SaaS landing pages, product-led growth strategies, and onboarding funnels.' },
              { icon: '👗', name: 'Fashion & Beauty', desc: 'Visually stunning brand sites, influencer campaigns, and Instagram ads.' },
              { icon: '⚽', name: 'Sports & Leisure', desc: 'Membership platforms, event booking, and social media community management.' },
              { icon: '🏦', name: 'Finance & Insurance', desc: 'Compliant fintech websites, lead gen funnels, and targeted PPC campaigns.' }].
              map((sector, i) =>
              <div
                key={i}
                className={`reveal-scale-up delay-${i % 4 * 100 + 100} p-5 rounded-2xl transition-all duration-300 hover:-translate-y-1 cursor-default group`}
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,122,255,0.15)' }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(0,198,255,0.4)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(0,122,255,0.15)'}>
                  <div className="text-3xl mb-2">{sector.icon}</div>
                  <h3 className="font-bold text-white text-sm mb-1">{sector.name}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{sector.desc}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── FAQ ─────────────────────────────────────────────────────── */}
        <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#00132B' }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Frequently Asked Questions</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 mt-4">Everything you need to know about working with Fusion Web Design</p>
            </div>
            <div className="space-y-4">
              {[
              { q: 'How much does a website cost?', a: 'Every project is unique, so we provide tailored quotes based on your specific requirements. As a general guide, a professional brochure website starts from £800, an e-commerce store from £1,500, and a bespoke web application from £3,000. All prices include responsive design, SEO setup, and 30 days of post-launch support. Contact us for a free, no-obligation quote.' },
              { q: 'How long does it take to build a website?', a: 'Most standard websites are completed within 2–4 weeks from the point of design sign-off. E-commerce and SaaS projects typically take 4–8 weeks. We work to agreed deadlines and keep you updated at every stage. Rush turnaround options are also available for time-sensitive launches.' },
              { q: 'Will my website rank on Google?', a: 'Every web design project we deliver is mobile-first, SEO-ready, and built for performance. We optimise heading structure, meta tags, page speed, mobile responsiveness, SSL, and XML sitemaps as standard. For sustained Google rankings, we recommend our ongoing SEO service, which includes keyword targeting, content creation, and link building.' },
              { q: 'Do you offer social media management?', a: 'Yes. We offer comprehensive social media management packages covering strategy, content creation, scheduling, community management, and paid advertising across Facebook, Instagram, TikTok, LinkedIn, and more. Whether you need a full-service management solution or targeted ad campaigns, we can help.' },
              { q: 'Can you help with Google Ads?', a: 'Absolutely. Our Google Ads service covers campaign setup, keyword research, ad copywriting, bid management, landing page optimisation, and monthly reporting. We manage budgets ranging from £300/month to £30,000+/month and focus on delivering the lowest possible cost-per-acquisition for your business.' },
              { q: 'Will I be able to update my website myself?', a: 'Yes. All our websites are built with user-friendly content management in mind. We can integrate Contentful, Sanity, or WordPress as a headless CMS so you can update text, images, blog posts, and products without any technical knowledge. We also provide training and documentation as part of every project.' },
              { q: 'Do you offer website hosting?', a: 'We can recommend and configure the best hosting solution for your needs — whether that is shared hosting, a managed VPS, or cloud hosting on AWS, Google Cloud, or Vercel. We ensure fast load times, automatic backups, SSL certificates, and 99.9% uptime SLAs as standard.' },
              { q: 'Do you work with businesses outside the UK?', a: 'Yes, we work with clients across the UK, Europe, and internationally. Whilst we are based in the UK, all of our communication and project management is conducted online, making it seamless to work with businesses anywhere in the world.' }].
              map((faq, i) =>
              <FaqItem key={i} question={faq.q} answer={faq.a} delay={i * 50} />
              )}
            </div>
          </div>
        </section>

        {/* ... keep existing code */}

        {/* ── Contact ────────────────────────────────────────────────── */}
        <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ backgroundColor: '#001F3F' }}>
          <div className="max-w-4xl mx-auto">

            <div className="text-center mb-16">
              <h2 className="reveal-up text-4xl md:text-5xl font-extrabold mb-4">Get In Touch</h2>
              <span className="section-heading-line mb-4" />
              <p className="reveal-up delay-200 text-xl text-gray-400 mt-4">
                Ready to start your project? Let's create something amazing together
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="reveal-scale-up delay-300 rounded-2xl p-8 md:p-12 space-y-6"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(0,122,255,0.2)',
                boxShadow: '0 8px 48px rgba(0,31,63,0.6)'
              }}>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="form-field">
                  <label className="block text-sm font-semibold mb-2 text-gray-300">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-xl px-4 py-3 text-white focus:outline-none transition-all duration-200"
                    style={{ background: 'rgba(0,31,63,0.8)', border: '1px solid rgba(0,122,255,0.25)' }}
                    placeholder="Your name"
                    onFocus={(e) => e.currentTarget.style.borderColor = '#007AFF'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(0,122,255,0.25)'} />
                </div>
                <div className="form-field">
                  <label className="block text-sm font-semibold mb-2 text-gray-300">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-xl px-4 py-3 text-white focus:outline-none transition-all duration-200"
                    style={{ background: 'rgba(0,31,63,0.8)', border: '1px solid rgba(0,122,255,0.25)' }}
                    placeholder="your@email.com"
                    onFocus={(e) => e.currentTarget.style.borderColor = '#007AFF'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(0,122,255,0.25)'} />
                </div>
              </div>

              <div className="form-field">
                <label className="block text-sm font-semibold mb-2 text-gray-300">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-xl px-4 py-3 text-white focus:outline-none transition-all duration-200"
                  style={{ background: 'rgba(0,31,63,0.8)', border: '1px solid rgba(0,122,255,0.25)' }}
                  placeholder="07346 071355"
                  onFocus={(e) => e.currentTarget.style.borderColor = '#007AFF'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(0,122,255,0.25)'} />
              </div>

              <div className="form-field">
                <label htmlFor="service-select" className="block text-sm font-semibold mb-2 text-gray-300">Service Interested In *</label>
                <select
                  id="service-select"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-xl px-4 py-3 text-white focus:outline-none transition-all duration-200"
                  style={{ background: 'rgba(0,31,63,0.8)', border: '1px solid rgba(0,122,255,0.25)' }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#007AFF'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(0,122,255,0.25)'}>
                  <option>Web Development</option>
                  <option>Google Marketing</option>
                  <option>Social Media Marketing</option>
                  <option>Full Digital Package</option>
                </select>
              </div>

              <div className="form-field">
                <label className="block text-sm font-semibold mb-2 text-gray-300">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full rounded-xl px-4 py-3 text-white focus:outline-none transition-all duration-200 resize-none"
                  style={{ background: 'rgba(0,31,63,0.8)', border: '1px solid rgba(0,122,255,0.25)' }}
                  placeholder="Tell us about your project..."
                  onFocus={(e) => e.currentTarget.style.borderColor = '#007AFF'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(0,122,255,0.25)'} />
              </div>

              {formMessage &&
              <div
                className="p-4 rounded-xl text-center font-medium transition-all duration-300"
                style={{
                  background: formStatus === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                  border: `1px solid ${formStatus === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  color: formStatus === 'success' ? '#22c55e' : '#ef4444'
                }}>
                  {formMessage}
                </div>
              }

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="btn-glow w-full py-4 rounded-xl font-semibold text-lg text-white transition-all duration-300 hover:opacity-90 hover:shadow-2xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(90deg,#007AFF,#00C6FF)', boxShadow: '0 4px 24px rgba(0,198,255,0.3)' }}>
                {formStatus === 'loading' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </section>

      </main>

      {/* ── AI Chat Widget ──────────────────────────────────────────── */}
      <div className="fixed bottom-24 right-6 z-50">
        {isChatOpen && (
          <div 
            className="mb-4 rounded-2xl overflow-hidden shadow-2xl"
            style={{
              width: '380px',
              maxWidth: 'calc(100vw - 48px)',
              height: '500px',
              background: 'rgba(0,31,63,0.98)',
              border: '1px solid rgba(0,198,255,0.3)',
              backdropFilter: 'blur(12px)'
            }}>
            {/* Chat Header */}
            <div 
              className="flex items-center justify-between px-5 py-4"
              style={{ background: 'linear-gradient(90deg,#007AFF,#00C6FF)', borderBottom: '1px solid rgba(0,198,255,0.3)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)' }}>
                  <span className="text-2xl">🤖</span>
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">AI Assistant</h3>
                  <p className="text-xs text-white/80">Ask me anything</p>
                </div>
              </div>
              <button
                onClick={() => setIsChatOpen(false)}
                className="text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors"
                aria-label="Close chat">
                <X size={20} />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex flex-col h-[calc(100%-140px)] overflow-y-auto p-4 space-y-3">
              {chatMessages.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-5xl mb-4">💬</div>
                  <p className="text-gray-400 text-sm">Hi! I'm here to help answer questions about our services, pricing, and more.</p>
                  <div className="mt-4 space-y-2">
                    {['What services do you offer?', 'How much does a website cost?', 'How long does it take?'].map((q, i) => (
                      <button
                        key={i}
                        onClick={() => {setChatInput(q); setChatMessages([{ role: 'user', content: q }]); handleChatSubmit(new Event('submit') as any);}}
                        className="block w-full text-left px-3 py-2 rounded-lg text-xs transition-colors"
                        style={{ background: 'rgba(0,122,255,0.15)', color: '#00C6FF', border: '1px solid rgba(0,198,255,0.3)' }}>
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
                    style={{
                      background: msg.role === 'user' 
                        ? 'linear-gradient(135deg,#007AFF,#00C6FF)' 
                        : 'rgba(255,255,255,0.08)',
                      color: '#fff',
                      borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px'
                    }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {isChatLoading && (
                <div className="flex justify-start">
                  <div className="px-4 py-2.5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0s' }} />
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.2s' }} />
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.4s' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <form onSubmit={handleChatSubmit} className="p-4 border-t" style={{ borderColor: 'rgba(0,198,255,0.2)' }}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Type your question..."
                  disabled={isChatLoading}
                  className="flex-1 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none transition-all"
                  style={{ background: 'rgba(0,31,63,0.9)', border: '1px solid rgba(0,122,255,0.3)' }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#007AFF'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(0,122,255,0.3)'} />
                <button
                  type="submit"
                  disabled={!chatInput.trim() || isChatLoading}
                  className="px-4 py-2.5 rounded-xl font-semibold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(90deg,#007AFF,#00C6FF)' }}
                  aria-label="Send message">
                  <ArrowRight size={18} />
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Chat Toggle Button */}
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="flex items-center justify-center w-14 h-14 rounded-full transition-all duration-300 hover:scale-110 shadow-xl"
          style={{ 
            background: isChatOpen ? 'rgba(239,68,68,0.95)' : 'linear-gradient(135deg,#007AFF,#00C6FF)',
            boxShadow: '0 4px 24px rgba(0,122,255,0.4)'
          }}
          aria-label={isChatOpen ? 'Close AI chat' : 'Open AI chat'}>
          {isChatOpen ? <X size={24} className="text-white" /> : (
            <span className="text-2xl">💬</span>
          )}
        </button>
      </div>

      {/* ── WhatsApp Button ─────────────────────────────────────────── */}
      <a
        href="https://wa.me/447346071355"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        className="whatsapp-btn fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full transition-transform duration-300 hover:scale-110"
        style={{ backgroundColor: '#25D366', boxShadow: '0 4px 20px rgba(37,211,102,0.4)' }}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="28" height="28">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#00132B', borderColor: 'rgba(0,122,255,0.15)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="reveal-up">
              <div className="flex items-center space-x-2 mb-4">
                <img
                  src="https://static.lumi.new/material/eb/ebfaca16edb4c088a017c193db3c4cde.png"
                  alt="Fusion Web Design"
                  width="160"
                  height="40"
                  sizes="160px"
                  className="h-10 w-auto object-contain transition-transform duration-300 hover:scale-105"
                  style={{ filter: 'brightness(0) invert(1)', maxWidth: '160px' }} />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Transforming businesses through innovative web solutions and digital marketing excellence.
              </p>
            </div>
            <div className="reveal-up delay-150">
              <h3 className="font-bold text-lg mb-4 text-white">Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                {['Web Development', 'Google Marketing', 'Social Media Marketing'].map((s) =>
                <li key={s} className="footer-link cursor-pointer">{s}</li>
                )}
              </ul>
            </div>
            <div className="reveal-up delay-300">
              <h3 className="font-bold text-lg mb-4 text-white">Contact</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <a href="https://fusionweb.website" target="_blank" rel="noopener noreferrer"
                  className="footer-link transition-colors duration-200" style={{ color: '#00C6FF' }}>
                    fusionweb.website
                  </a>
                </li>
                <li className="footer-link">fusionwebdes@gmail.com</li>
                <li>
                  <a href="https://wa.me/447346071355" target="_blank" rel="noopener noreferrer"
                  className="footer-link flex items-center space-x-2 transition-colors duration-200">
                    <span style={{ color: '#25D366' }}>●</span>
                    <span>WhatsApp: 07346 071355</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8 text-center text-gray-500 text-sm reveal-up delay-400" style={{ borderColor: 'rgba(0,122,255,0.1)' }}>
            <p>&copy; 2025 <a href="https://fusionweb.website" target="_blank" rel="noopener noreferrer" style={{ color: '#00C6FF' }} className="hover:opacity-80 transition-opacity duration-200">fusionweb.website</a> — Fusion Web Design. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>);

}

export default Home;